// Relative "when did this last happen" labels, ported from plus-one:
//   formatRelativeAge  compact duration tokens: 30m, 1h, 12h, 4d, 1w, 5mo, 1y.
//                      Same calendar day is elapsed minutes or hours, never
//                      "Today"; older stamps use calendar days.
//   formatRelativeDate calendar buckets: Today, Yesterday, weekday, then a
//                      compact date.
//   formatCompactDate  "Jul 26" or "Jul 26, 2025".
// `now` is explicit so every rule is deterministic under test.

export function formatRelativeDate(date: Date, now: Date = new Date()): string {
  const dayDiff = calendarDayDiff(now, date)
  if (dayDiff === 0) return 'Today'
  if (dayDiff === 1) return 'Yesterday'
  if (dayDiff >= 2 && dayDiff <= 6) return date.toLocaleDateString('en-US', { weekday: 'long' })
  return formatCompactDate(date, now)
}

export function formatCompactDate(date: Date, now: Date = new Date()): string {
  if (Number.isNaN(date.getTime())) return ''
  const sameYear = date.getFullYear() === now.getFullYear()
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) })
}

const AGE_ABBREV = { minute: 'm', hour: 'h', day: 'd', week: 'w', month: 'mo', year: 'y' } as const

type AgeUnit = keyof typeof AGE_ABBREV

const MS_PER_MINUTE = 60 * 1000
const MS_PER_HOUR = 60 * MS_PER_MINUTE

export function formatRelativeAge(date: Date, now: Date = new Date()): string {
  const parts = relativeAgeParts(date, now)
  return parts === null ? '' : `${parts.count}${AGE_ABBREV[parts.unit]}`
}

function relativeAgeParts(date: Date, now: Date): { unit: AgeUnit; count: number } | null {
  if (Number.isNaN(date.getTime())) return null
  const days = calendarDayDiff(now, date)
  if (days < 0) return { unit: 'minute', count: 1 }
  if (days === 0) {
    const elapsed = Math.max(0, now.getTime() - date.getTime())
    if (elapsed < MS_PER_HOUR) return { unit: 'minute', count: Math.max(1, Math.floor(elapsed / MS_PER_MINUTE)) }
    return { unit: 'hour', count: Math.floor(elapsed / MS_PER_HOUR) }
  }
  if (days < 7) return { unit: 'day', count: days }
  if (days < 30) return { unit: 'week', count: Math.floor(days / 7) }
  if (days < 365) return { unit: 'month', count: Math.floor(days / 30) }
  return { unit: 'year', count: Math.floor(days / 365) }
}

/** Local-midnight-to-local-midnight day count (a minus b). */
function calendarDayDiff(a: Date, b: Date): number {
  const startOfLocalDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  return Math.round((startOfLocalDay(a) - startOfLocalDay(b)) / (24 * 60 * 60 * 1000))
}
